import { useState } from 'react';
import { Calendar, Camera, Eye, Tag, CheckSquare, Square } from 'lucide-react';
import { PhotoCardProps } from '../types/photo';

export const ImpactPhotoCard: React.FC<PhotoCardProps> = ({ 
  photo, 
  viewMode, 
  onClick, 
  selected = false,
  isSelectionMode = false,
  onSelect,
  selectionDisabled = false
}) => {
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid Date';
    }
  };


  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Selected':
        return 'bg-green-100 text-green-800';
      case 'Under Review':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleCardClick = () => {
    // Always show detail modal when clicking on the image area
    onClick();
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSelect) {
      onSelect(photo.image_id);
    }
  };

  const canBeSelected = photo.status === 'Under Review' && !selectionDisabled;

  if (viewMode === 'grid') {
    return (
      <div 
        className={`
          group relative bg-white rounded-lg border-2 overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg
          ${selected ? 'border-blue-500 shadow-lg' : 'border-gray-200 hover:border-gray-300'}
          ${!canBeSelected && isSelectionMode ? 'opacity-50' : ''}
        `}
        onClick={handleCardClick}
      >
        {/* Image */}
        <div className="aspect-square relative overflow-hidden bg-gray-100">
          {!imageError ? (
            <>
              {imageLoading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Camera className="h-8 w-8 text-gray-400 animate-pulse" />
                </div>
              )}
              <img
                src={photo.asset_url}
                alt={`${photo.program} - ${photo.activity}`}
                className={`w-full h-full object-cover transition-opacity duration-200 ${
                  imageLoading ? 'opacity-0' : 'opacity-100'
                }`}
                onLoad={() => setImageLoading(false)}
                onError={() => {
                  setImageError(true);
                  setImageLoading(false);
                }}
              />
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <Camera className="h-8 w-8 text-gray-400" />
              <span className="sr-only">Image failed to load</span>
            </div>
          )}
          
          {/* Overlay */}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
            <Eye className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
          </div>
          
          {/* Selection Checkbox */}
          {isSelectionMode && canBeSelected && (
            <div className="absolute top-2 left-2">
              <button
                onClick={handleCheckboxClick}
                className="p-1 bg-white rounded-md shadow-sm hover:bg-gray-50 transition-colors"
              >
                {selected ? (
                  <CheckSquare className="h-4 w-4 text-blue-600" />
                ) : (
                  <Square className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
          )}

          {/* Status Badge */}
          <div className={`absolute top-2 ${isSelectionMode && canBeSelected ? 'right-2' : 'right-2'}`}>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(photo.status)}`}>
              {photo.status}
            </span>
          </div>
        </div>

        {/* Info */}
        <div className="p-3 space-y-2">
          <div className="flex items-center text-xs text-gray-600">
            <Tag className="h-3 w-3 mr-1" />
            <span className="font-medium text-blue-600">{photo.program}</span>
            <span className="mx-1">•</span>
            <span className="truncate">{photo.activity}</span>
          </div>
          
          <div className="flex items-center text-xs text-gray-500">
            <Calendar className="h-3 w-3 mr-1" />
            <span>{formatDate(photo.date)}</span>
          </div>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div 
      className={`
        group flex items-center p-4 bg-white rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-md
        ${selected ? 'border-blue-500 shadow-md' : 'border-gray-200 hover:border-gray-300'}
        ${!canBeSelected && isSelectionMode ? 'opacity-50' : ''}
      `}
      onClick={handleCardClick}
    >
      {/* Thumbnail */}
      <div className="flex-shrink-0 w-20 h-20 relative overflow-hidden bg-gray-100 rounded-lg">
        {!imageError ? (
          <>
            {imageLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Camera className="h-6 w-6 text-gray-400 animate-pulse" />
              </div>
            )}
            <img
              src={photo.asset_url}
              alt={`${photo.program} - ${photo.activity}`}
              className={`w-full h-full object-cover transition-opacity duration-200 ${
                imageLoading ? 'opacity-0' : 'opacity-100'
              }`}
              onLoad={() => setImageLoading(false)}
              onError={() => {
                setImageError(true);
                setImageLoading(false);
              }}
            />
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Camera className="h-6 w-6 text-gray-400" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 ml-4 min-w-0">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-semibold text-blue-600">{photo.program}</span>
              <span className="text-sm text-gray-400">•</span>
              <span className="text-sm text-gray-600 truncate">{photo.activity}</span>
            </div>
            
            <div className="flex items-center text-xs text-gray-500">
              <Calendar className="h-3 w-3 mr-1" />
              <span>{formatDate(photo.date)}</span>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {isSelectionMode && canBeSelected && (
              <button
                onClick={handleCheckboxClick}
                className="p-1 hover:bg-gray-50 rounded transition-colors"
              >
                {selected ? (
                  <CheckSquare className="h-4 w-4 text-blue-600" />
                ) : (
                  <Square className="h-4 w-4 text-gray-400" />
                )}
              </button>
            )}
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(photo.status)}`}>
              {photo.status}
            </span>
            <Eye className="h-4 w-4 text-gray-400 group-hover:text-gray-600" />
          </div>
        </div>
      </div>
    </div>
  );
};